import {MEDIA_LIBRARY_KEYS} from "@/api/mediaLibraryApi";
import {createGalleryStore} from "@/store/campaignGallery";
import {create} from "@/store/createStore";
import type {TGalleryFilters, TMediaLibraryScope, TMediaLibraryTab} from "@/store/types";
import type {QueryKey} from "@tanstack/react-query";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

type TMediaLibraryFilters = TGalleryFilters & {
  campaign_ids: number[];
};

const initialFilters: TMediaLibraryFilters = {
  campaign_ids: [],
  date: [],
  gender: [],
  has_code: null,
  liked: null,
  media_product_type: null,
  ordering: null,
  purchased: null,
  search: "",
};

const TABS: TMediaLibraryTab[] = ["instagram", "source", "tiktok", "ugc"];

const createTabStore = (tab: TMediaLibraryTab) =>
  createGalleryStore<TMediaLibraryFilters>({...initialFilters}, `MediaLibrary${tab}`, (searchParams): QueryKey => {
    const {agencyId, companyId} = useMediaLibraryStore.getState();
    return MEDIA_LIBRARY_KEYS.qMediaLibrary({agencyId, companyId, params: searchParams});
  });

type TStoreReturnType = ReturnType<typeof createTabStore>;

type TState = {
  active: TMediaLibraryTab;
  agencyId?: number;
  companyId?: number;
  instagram: TStoreReturnType;
  source: TStoreReturnType;
  tiktok: TStoreReturnType;
  ugc: TStoreReturnType;
  setActiveTab(tab: TMediaLibraryTab): void;
  setRouteIds(ids: TMediaLibraryScope): void;
};

const useMediaLibraryStore = create<TState>()(
  immer(
    devtools(
      set => ({
        active: "instagram",
        instagram: createTabStore("instagram"),
        source: createTabStore("source"),
        tiktok: createTabStore("tiktok"),
        ugc: createTabStore("ugc"),
        setActiveTab: tab =>
          set(
            state => {
              state.active = tab;
            },
            false,
            "useMediaLibraryStore/setActiveTab",
          ),
        setRouteIds: ({agencyId, companyId}) => {
          const previous = useMediaLibraryStore.getState();

          if (previous.agencyId !== agencyId || previous.companyId !== companyId) {
            TABS.forEach(tab => previous[tab].getState().reset());
          }

          set(
            state => {
              state.agencyId = agencyId;
              state.companyId = companyId;
            },
            false,
            "useMediaLibraryStore/setRouteIds",
          );
        },
      }),
      {name: "useMediaLibraryStore"},
    ),
  ),
);

export {useMediaLibraryStore};
